import { FIREBASE_API_UPGRADE_DOCS_LINK } from 'modules/mobileApps/constants';
import {
  IntegrationFormField,
  useIntegrationFormContext,
} from 'modules/mobileApps/context/integrationForm';
import {
  IntegrationOperationType,
  useIntegrationMetadataContext,
} from 'modules/mobileApps/context/integrationMetadata';
import { AppPlatform, IntegrationAuthType } from 'modules/mobileApps/types';
import { getIsLegacyFirebaseIntegration } from 'modules/mobileApps/utils';
import { spacing } from 'ui/theme';
import { StyledBanner, StyledLink } from './FirebaseIntegrationForm.styled';
import { FileDropzoneField, NotificationTypeField } from './components';

export const FIREBASE_INTEGRATION_FORM_ID = 'firebase-integration-form';

const SandboxTestingWarningBanner = () => (
  <div style={{ marginTop: spacing.plusHalf }}>
    <StyledBanner data-test="sandbox-testing-warning-banner" title="Sandbox testing recommended">
      {`Prior to uploading your JSON credentials into a production project, we recommend testing in a
    sandbox project first to prevent any interruptions to your push campaign sends. `}
      <StyledLink href={FIREBASE_API_UPGRADE_DOCS_LINK}>Learn more</StyledLink>
    </StyledBanner>
  </div>
);

const HelpText = () => (
  <>
    {'You can generate JSON credentials in your Firebase console. Visit our '}
    <StyledLink href={FIREBASE_API_UPGRADE_DOCS_LINK} rel="noreferrer noopener" target="_blank">
      support docs
    </StyledLink>
    {' to learn more.'}
  </>
);

export const FirebaseIntegrationForm = () => {
  const { watch } = useIntegrationFormContext();
  const { appDetails, operationType, selectedIntegration } = useIntegrationMetadataContext();

  const authType = watch(IntegrationFormField.authType);
  const isAndroid = appDetails?.platform === AppPlatform.ANDROID;
  const isFirebaseJson = authType === IntegrationAuthType.FIREBASE_JSON;
  const isUpdateOperation = operationType === IntegrationOperationType.UPDATE;

  const hasLegacyFirebase =
    !!selectedIntegration &&
    getIsLegacyFirebaseIntegration(selectedIntegration.platform, selectedIntegration.authType);
  const isReplacingLegacyFirebase = isFirebaseJson && isUpdateOperation && hasLegacyFirebase;

  return (
    <div data-test={FIREBASE_INTEGRATION_FORM_ID}>
      {isAndroid && (
        <NotificationTypeField data-test={`${FIREBASE_INTEGRATION_FORM_ID}-use-os-notifications`} />
      )}
      {isReplacingLegacyFirebase && <SandboxTestingWarningBanner />}
      <FileDropzoneField
        data-test={`${FIREBASE_INTEGRATION_FORM_ID}-upload-json-file`}
        accept={{ 'application/json': ['.json'] }}
        helpText={<HelpText />}
        labelText="Upload JSON credentials"
        name={IntegrationFormField.firebaseJsonFile}
      />
    </div>
  );
};
